if (!customElements.get('sc-announcement-marquee')) {
  class SCAnnouncementMarquee extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.storageKey = `announcement-marquee-dismissed-${this.section.id}`;
      this.slider = this.querySelector('.sc-announcement-marquee__slider');
      this.slides = [...this.querySelectorAll('.sc-announcement-marquee__text')];
      this.index = 0;
      this.editorPaused = false;
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      this.querySelector('.js-announcement-prev')?.addEventListener('click', () => this.step(-1), options);
      this.querySelector('.js-announcement-next')?.addEventListener('click', () => this.step(1), options);
      this.querySelector('.js-announcement-dismiss')?.addEventListener('click', () => this.dismiss(), options);
      window.addEventListener('on:breakpoint-change', () => this.goTo(this.index, 'instant'), options);
      this.slider?.addEventListener('scroll', () => this.syncSlide(), { ...options, passive: true });
      this.section.addEventListener('shopify:block:select', (event) => {
        this.editorPaused = true;
        this.classList.add('is-paused');
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.goTo(index, 'instant');
        const item = this.querySelector(`.sc-announcement-marquee__marquee-item[data-block-id="${event.detail.blockId}"]`);
        item?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      }, options);
      this.section.addEventListener('shopify:block:deselect', () => {
        this.editorPaused = false;
        this.classList.remove('is-paused');
      }, options);
      this.section.addEventListener('shopify:section:select', () => {
        this.section.classList.remove('sc-announcement-marquee--dismissed');
        this.scheduleSlide();
        this.updateHeaderLayout();
      }, options);

      // Storage may be unavailable in restricted/private browsing contexts.
      try {
        if (!window.Shopify?.designMode && this.querySelector('.js-announcement-dismiss')
          && theme.storageUtil.get(this.storageKey, false, true) === '1') {
          this.section.classList.add('sc-announcement-marquee--dismissed');
          this.updateHeaderLayout();
        }
      } catch { /* Keep the bar usable without storage. */ }

      this.goTo(this.index, 'instant');
      this.resizeObserver = new ResizeObserver(() => this.goTo(this.index, 'instant'));
      if (this.slider) this.resizeObserver.observe(this.slider);
      this.scheduleSlide();
    }

    disconnectedCallback() {
      clearTimeout(this.slideTimer);
      this.events.abort();
      this.resizeObserver.disconnect();
    }

    reducedMotion() {
      return getComputedStyle(this).getPropertyValue('--announcement-reduced-motion').trim() === '1';
    }

    scheduleSlide() {
      clearTimeout(this.slideTimer);
      if (!this.slider || this.slides.length < 2) return;
      this.slideTimer = setTimeout(() => {
        if (!this.isConnected) return;
        if (!document.hidden && !this.editorPaused && !this.reducedMotion()
          && !this.matches(':hover, :focus-within') && this.slider.clientWidth
          && window.theme?.elementUtil?.isInViewport?.(this) !== false) {
          this.goTo((this.index + 1) % this.slides.length);
        }
        this.scheduleSlide();
      }, Number(this.dataset.slideDelay) || 4000);
    }

    step(offset) {
      if (!this.slides.length) return;
      this.goTo((this.index + offset + this.slides.length) % this.slides.length);
      this.scheduleSlide();
    }

    goTo(index, behavior = 'smooth') {
      if (!this.slider || !this.slides[index]) return;
      this.index = index;
      const direction = getComputedStyle(this.slider).direction === 'rtl' ? -1 : 1;
      this.slider.scrollTo({
        left: direction * index * this.slider.clientWidth,
        behavior: this.reducedMotion() ? 'instant' : behavior
      });
      this.markSlide(index);
    }

    syncSlide() {
      if (!this.slider.clientWidth) return;
      this.index = Math.min(this.slides.length - 1,
        Math.round(Math.abs(this.slider.scrollLeft) / this.slider.clientWidth));
      this.markSlide(this.index);
    }

    markSlide(index) {
      this.slides.forEach((slide, i) => {
        slide.inert = i !== index;
        slide.setAttribute('aria-hidden', String(i !== index));
      });
    }

    dismiss() {
      this.section.classList.add('sc-announcement-marquee--dismissed');
      clearTimeout(this.slideTimer);
      if (!window.Shopify?.designMode) {
        try {
          theme.storageUtil.set(this.storageKey, '1', true);
        } catch { /* Dismiss this page even when storage is unavailable. */ }
      }
      this.updateHeaderLayout();
    }

    updateHeaderLayout() {
      const header = document.querySelector('store-header');
      header?.updateHeaderHeights?.();
      header?.setHeaderEnd?.();
      window.dispatchEvent(new Event('resize'));
    }

  }

  customElements.define('sc-announcement-marquee', SCAnnouncementMarquee);
}
